/* eslint-disable */

import { useMutation, useQuery } from 'react-query';
import axios from 'axios';
import { logOutUser, generateToastError } from '../../utils';
import { queryKeys } from './queryKeys';

// all the react query requests will start from here

const httpStatuses = { unauthorized: 401, error: 500 };

const refreshProfileData = async (userToken, updateProfileData) => {
  try {
    const profileResponse = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/auth/profile`, {
      headers: { Authorization: `Bearer ${userToken}` },
    });

    if (profileResponse?.data?.data) {
      const currentUserData = JSON.parse(localStorage.getItem('user')) || {};
      const token = currentUserData.token || userToken;
      // Prefer API value for tour state (source of truth); fallback to existing localStorage
      const hasCompletedTour =
        typeof profileResponse.data.data?.hasCompletedTour === 'boolean'
          ? profileResponse.data.data.hasCompletedTour
          : currentUserData?.hasCompletedTour;

      const updatedUserData = {
        ...profileResponse.data.data,
        token,
        ...(typeof hasCompletedTour === 'boolean' && { hasCompletedTour }),
      };

      localStorage.setItem('user', JSON.stringify(updatedUserData));

      if (updateProfileData) {
        updateProfileData(profileResponse.data.data);
      }
    }
  } catch (profileError) {
    console.error('Error fetching updated profile:', profileError);
  }
};

export const useMutationOnboardRole = (payload, toast, handleClose, userRole, queryClient, navigate) => {
  const queryKey = [queryKeys.onboard, payload];
  const queryFn = () =>
    axios.post(`${process.env.REACT_APP_SERVER_URL}/api/auth/register`, payload, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

  return useMutation(queryKey, queryFn, {
    enabled: false,
    refetchOnWindowFocus: false,
    onSuccess: (success) => {
      if (userRole && userRole === 'admin') {
        toast.success(
          `${success.data.message}, This account will be activated once user verify their identity using their email`,
          {
            duration: 5000,
          }
        );
        handleClose();
        queryClient.invalidateQueries({ queryKey: [queryKeys.getCoachesList] });

        return;
      }
      // if (success.data.data.signingUrl) {
      //   // Open the signing URL in a new tab
      //   toast.success(`${success.data.message}, For activating your account please check your email`, {
      //     duration: 5000,
      //   });
      //   navigate('/verify-docusign');
      //   setTimeout(() => {
      //     window.open(success.data.data.signingUrl, '_blank');
      //   }, 5000);
      // } 
      else {
        toast.success(`${success.data.message}, For activating your account please check your email`, {
          duration: 5000,
        });

        // Redirect to login
        navigate('/login');
      }
    },
    onError: (error) => {
      try {
        generateToastError(toast, error);

        // if (userRole && userRole === 'admin') {
        //   handleClose();
        // }
      } catch (e) {
        toast.error('The errror ', e);
      }
    },
  });
};

export const useQueryVerifyUserEmail = (payload, onSuccess, onError) => {
  const queryKey = [queryKeys.verifyUser, payload];
  const queryFn = ({ queryKey }) =>
    axios.post(`${process.env.REACT_APP_SERVER_URL}/api/auth/verify-email`, queryKey[1]);

  return useQuery(queryKey, queryFn, {
    enabled: true,
    retry: 0,
    refetchOnWindowFocus: false,

    staleTime: Infinity,

    onSuccess,
    onError,
  });
};

export const useMutationChangeProfileImage = (
  toast,
  userToken,
  navigate,
  handleClose,
  queryClient,
  updateProfileData,
  refreshProfile
) => {
  return useMutation(
    (formData) =>
      axios.post(`${process.env.REACT_APP_SERVER_URL}/api/auth/change-image`, formData, {
        headers: {
          Authorization: `Bearer ${userToken}`,
          'Content-Type': 'multipart/form-data',
        },
      }),
    {
      onSuccess: async (success) => {
        toast.success(`${success?.data?.message}`, {
          duration: 3000,
        });
        handleClose();
        // Invalidate query first to mark it as stale
        if (queryClient) {
          await queryClient.invalidateQueries({ queryKey: [queryKeys.getUserProfile] });
        }
        // Then refetch to get fresh data
        if (refreshProfile) {
          await refreshProfile();
        }
        // Also update localStorage directly for immediate UI update
        await refreshProfileData(userToken, updateProfileData);
      },
      onError: (e) => {
        if (e?.response?.status === httpStatuses.unauthorized) {
          logOutUser(navigate);
          return;
        }
        generateToastError(toast, e);
      },
    }
  );
};

export const useMutationChangeProfileVideo = (
  toast,
  userToken,
  navigate,
  setPreviewVideo,
  setSelectedVideo,
  setOpenUploadVideo,
  queryClient,
  updateProfileData,
  refreshProfile
) => {
  return useMutation(
    (formData) =>
      axios.post(`${process.env.REACT_APP_SERVER_URL}/api/auth/change-video`, formData, {
        headers: {
          Authorization: `Bearer ${userToken}`,
          'Content-Type': 'multipart/form-data',
        },
      }),
    {
      onSuccess: async (success) => {
        toast.success(`${success?.data?.message}`, {
          duration: 3000,
        });
        const videoUrl = success?.data?.data?.video;
        setSelectedVideo(videoUrl || '');
        setPreviewVideo('');
        setOpenUploadVideo(false);
        // Invalidate query first to mark it as stale
        if (queryClient) {
          await queryClient.invalidateQueries({ queryKey: [queryKeys.getUserProfile] });
        }
        // Then refetch to get fresh data
        if (refreshProfile) {
          await refreshProfile();
        }
        // Also update localStorage directly for immediate UI update
        await refreshProfileData(userToken, updateProfileData);
      },
      onError: (e) => {
        if (e?.response?.status === httpStatuses.unauthorized) {
          logOutUser(navigate);
          return;
        }
        generateToastError(toast, e);
      },
    }
  );
};

export const useMutationSaveYoutubeLink = (
  toast,
  userToken,
  navigate,
  setOpenUploadVideo,
  queryClient,
  updateProfileData,
  refreshProfile
) => {
  return useMutation(
    (youtubeUrl) =>
      axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/auth/change-video`,
        { youtubeUrl },
        { headers: { Authorization: `Bearer ${userToken}` } }
      ),
    {
      onSuccess: async (success) => {
        toast.success(`${success?.data?.message}`, { duration: 3000 });
        setOpenUploadVideo(false);
        if (queryClient) {
          await queryClient.invalidateQueries({ queryKey: [queryKeys.getUserProfile] });
        }
        if (refreshProfile) {
          await refreshProfile();
        }
        await refreshProfileData(userToken, updateProfileData);
      },
      onError: (e) => {
        if (e?.response?.status === httpStatuses.unauthorized) {
          logOutUser(navigate);
          return;
        }
        generateToastError(toast, e);
      },
    }
  );
};

export const useMutationChangePassword = (
  toast,
  userToken,
  navigate,
  handleClose
) => {
  return useMutation(
    (payload) =>
      axios.post(`${process.env.REACT_APP_SERVER_URL}/api/auth/change-password`, payload, {
        headers: { Authorization: `Bearer ${userToken}` },
      }),
    {
      onSuccess: (success) => {
        toast.success(`${success?.data?.message}`, {
          duration: 3000,
        });
        handleClose();
      },
      onError: (e) => {
        if (e?.response?.status === httpStatuses.unauthorized) {
          logOutUser(navigate);
          return;
        }
        generateToastError(toast, e);
      },
    }
  );
};


export const useMutationChangeUserImage = (
  toast,
  userToken,
  navigate,
  setChangeImage,
  setPreviewImage,
  queryClient
) => {
  return useMutation(
    async ({ userId, formData }) => {
      if (!userId) {
        throw new Error("User ID is missing");
      }

      if (!formData) {
        throw new Error("Form data is missing");
      }

      return axios.put(
        `${process.env.REACT_APP_SERVER_URL}/api/admin/update-image/${userId}`, // Use userId in params
        formData, // Send only formData in body
        {
          headers: {
            Authorization: `Bearer ${userToken}`,
            "Content-Type": "multipart/form-data", // Required for file uploads
          },
        }
      );
    },
    {
      refetchOnWindowFocus: false,
      onSuccess: (success) => {
        toast.success(`${success?.data?.message}`, {
          duration: 3000,
        });
        setChangeImage(false);
        setPreviewImage('');
        
        // Invalidate and refetch the coaches list to show updated images
        if (queryClient) {
          queryClient.invalidateQueries({ queryKey: [queryKeys.getCoachesList] });
        }
      },
      onError: (e) => {
        if (e?.response?.status === 401) {
          logOutUser(navigate);
          return;
        }
        generateToastError(toast, e);
      },
    }
  );
};

export const useQueryVerifyLoginEmail = (payload, onSuccess, onError) => {
  const queryKey = [queryKeys.verifyCredentials, payload];
  const queryFn = ({ queryKey }) => axios.post(`${process.env.REACT_APP_SERVER_URL}/api/auth/login`, queryKey[1]);

  return useQuery(queryKey, queryFn, {
    enabled: false,
    retry: 0,
    refetchOnWindowFocus: false,

    staleTime: Infinity,

    onSuccess,
    onError,
  });
};

export const useMutationForgotPassword = (payload, toast, setIsLogin) => {
  const queryKey = [queryKeys.forgotPassword, payload];
  const queryFn = () => axios.post(`${process.env.REACT_APP_SERVER_URL}/api/auth/forgot-password`, payload);

  return useMutation(queryKey, queryFn, {
    enabled: false,
    retry: 0,
    refetchOnWindowFocus: false,

    staleTime: Infinity,

    onSuccess: (success) => {
      toast.success(`${success?.data?.message}`, {
        duration: 3000,
      });
      setIsLogin(true);
    },
    onError: (error) => {
      if (error?.response?.status === httpStatuses.unauthorized && payload?.role === 'admin') {
        logOutUser(navigate);
        return;
      }
      generateToastError(toast, error);
    },
  });
};

export const useQueryResetPassword = (payload, toast, navigate) => {
  const queryKey = [queryKeys.resetPassword, payload];
  const queryFn = () => axios.post(`${process.env.REACT_APP_SERVER_URL}/api/auth/reset-password`, payload);

  return useQuery(queryKey, queryFn, {
    enabled: false,
    retry: 0,
    refetchOnWindowFocus: false,

    staleTime: Infinity,

    onSuccess: (success) => {
      toast.success(`${success?.data?.message}`, {
        duration: 2000,
      });
      navigate('/login');
    },
    onError: (error) => {
      if (error?.response?.status === httpStatuses.unauthorized && payload?.role === 'admin') {
        logOutUser(navigate);
        return;
      }
      generateToastError(toast, error);
    },
  });
};

// coaches add edit delete flow
export const useQueryGetCoachesList = (payload, navigate, toast) => {
  const queryKey = [queryKeys.getCoachesList, payload];

  const queryFn = ({ queryKey }) => {
    const { token, role, page = 1, limit = 10, ...otherParams } = queryKey[1];
    
    // Build query parameters for pagination
    const params = {
      page,
      limit,
      ...otherParams
    };

    return axios.get(
      role === 'teacher'
        ? `${process.env.REACT_APP_SERVER_URL}/api/teacher/get-edifiers`
        : role === 'admin'
        ? `${process.env.REACT_APP_SERVER_URL}/api/admin/users`
        : role === 'site-leader'
        ? `${process.env.REACT_APP_SERVER_URL}/api/site-leader/site-users`
        : `${process.env.REACT_APP_SERVER_URL}/api/admin/users`,
      {
        headers: { Authorization: `Bearer ${token}` },
        params
      }
    );
  };

  return useQuery(queryKey, !payload.token ? logOutUser(navigate) : queryFn, {
    enabled: true,
    retry: 0,
    cacheTime: 3572000,

    refetchOnWindowFocus: false,

    staleTime: 0,

    onError: (e) => {
      if (e?.response?.status === httpStatuses.unauthorized && payload.role === 'admin') {
        logOutUser(navigate);
        return;
      }
      generateToastError(toast, e);
    },
  });
};

export const useQueryGetTeachersList = (userToken, navigate, toast) => {
  const queryKey = [queryKeys.getTeachersList];

  const queryFn = ({ queryKey }) =>
    axios.get(`${process.env.REACT_APP_SERVER_URL}/api/teacher/get-pairs`, {
      headers: { Authorization: `Bearer ${userToken}` },
    });
  return useQuery(queryKey, queryFn, {
    enabled: true,
    retry: 0,
    refetchOnWindowFocus: false,
    cacheTime: 72000,

    staleTime: Infinity,

    onError: (e) => {
      if (e?.response?.status === httpStatuses.unauthorized && payload.role === 'admin') {
        logOutUser(navigate);
        return;
      }
      generateToastError(toast, e);
    },
  });
};

export const useQueryDeleteCoach = (payload, navigate, setDeleteOperationOpen, toast, queryClient, userRole) => {
  const queryKey = [queryKeys.deleteCoach, payload];

  const queryFn = ({ queryKey }) =>
    axios.delete(
      `${process.env.REACT_APP_SERVER_URL}${
        userRole !== 'site-leader'
          ? `/api/admin/coach/delete/${queryKey[1]._id}`
          : `/api/site-leader/user/${queryKey[1]._id}`
      }`,
      {
        headers: { Authorization: `Bearer ${queryKey[1].token}` },
      }
    );

  return useQuery(queryKey, !payload.token ? logOutUser(navigate) : queryFn, {
    enabled: false,
    retry: 0,
    refetchOnWindowFocus: false,

    staleTime: Infinity,
    onSuccess: () => {
      setDeleteOperationOpen(false);
      if (userRole === 'site-leader') {
        toast.success('Request sent to admin');
        return;
      }
      queryClient.invalidateQueries({ queryKey: [queryKeys.getCoachesList] });
    },

    onError: (e) => {
      setDeleteOperationOpen(false);

      if (e?.message?.includes('Network Error')) {
        toast.error('Backend Service Not Found');
        return;
      }

      if (e?.response?.status === httpStatuses.unauthorized) {
        logOutUser(navigate);
        return;
      }

      if (e?.response?.status === httpStatuses.error) {
        toast.error('Either Server is Down or passed Item is already deleted');
        return;
      }
      generateToastError(toast, e);
    },
  });
};

export const useMutationModifyCoachRecord = (
  payload,
  toast,
  handleClose,
  userToken,
  setEdifierToEditData,
  navigate,
  queryClient,
  userRole
) => {
  const queryKey = [queryKeys.modifyCoachRecord, payload];
  const token = userToken || JSON.parse(localStorage.getItem('user') || '{}')?.token;
  const queryFn = () =>
    axios.put(
      userRole === 'site-leader'
        ? `${process.env.REACT_APP_SERVER_URL}/api/site-leader/user`
        : `${process.env.REACT_APP_SERVER_URL}/api/admin/coach/update`,
      payload,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

  return useMutation(queryKey, queryFn, {
    enabled: false,
    refetchOnWindowFocus: false,
    onSuccess: (success) => {
      toast.success(`${success?.data?.message}`, {
        duration: 3000,
      });
      if (userRole !== 'site-leader') {
        queryClient.invalidateQueries({ queryKey: [queryKeys.getCoachesList] });
      }
      setEdifierToEditData({});

      handleClose();
    },
    onError: (error) => {
      if (error?.response?.status === httpStatuses?.unauthorized && payload?.role === 'admin') {
        logOutUser(navigate);
        return;
      }
      generateToastError(toast, error);
    },
  });
};

// coaches add edit delete flow

// Manage Credentials Change Role Here

export const useMutationManageCredentials = (
  payload,
  toast,
  handleClose,
  userToken,
  setUserToEdit,
  navigate,
  queryClient
) => {
  const queryKey = [queryKeys.manageCredentials, payload];
  const queryFn = () =>
    axios.put(`${process.env.REACT_APP_SERVER_URL}/api/admin/update-credentials`, payload, {
      headers: { Authorization: `Bearer ${userToken}` },
    });

  return useMutation(queryKey, queryFn, {
    enabled: false,
    refetchOnWindowFocus: false,
    onSuccess: (success) => {
      toast.success(`${success.data.message}`, {
        duration: 5000,
      });
      setUserToEdit({});
      queryClient.invalidateQueries({ queryKey: [queryKeys.getAllUsers] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.getCoachesList] });

      handleClose();
    },
    onError: (error) => {
      try {
        if (error?.response?.status === httpStatuses.unauthorized && payload.role === 'admin') {
          logOutUser(navigate);
          return;
        }
        generateToastError(toast, error);
      } catch (e) {
        toast.error('The errror ', e);
      }
    },
  });
};


export const useMutationSwitchRole = (
  payload,
  toast,
  userToken,
  userRole,
  navigate
) => {
  const queryKey = [queryKeys.switchRole, payload];
  const queryFn = () =>
    axios.post(
      userRole === 'SITE-LEADER'
        ? `${process.env.REACT_APP_SERVER_URL}/api/site-leader/switch-role`
        : `${process.env.REACT_APP_SERVER_URL}/api/edifier/switch-role`,
      payload,
      {
        headers: { Authorization: `Bearer ${userToken}` },
      }
    );


  return useMutation(queryKey, queryFn, {
    enabled: false,
    refetchOnWindowFocus: true,
    onSuccess: (success) => {
      toast.success(`${success.data.message}`, {
        duration: 5000,
      });

      logOutUser(navigate);
      
    
    },
    onError: (error) => {
      try {
        if (error?.response?.status === httpStatuses.unauthorized && payload.role === 'admin') {
          logOutUser(navigate);
          return;
        }
        generateToastError(toast, error);
      } catch (e) {
        toast.error('The errror ', e);
      }
    },
  });
};

export const useQueryGetUsersList = (payload, navigate, toast) => {
  const queryKey = [queryKeys.getAllUsers, payload];

  const queryFn = ({ queryKey }) => {
    const { token, page = 1, limit = 10, ...otherParams } = queryKey[1];
    
    // Build query parameters for pagination
    const params = {
      page,
      limit,
      ...otherParams
    };

    return axios.get(`${process.env.REACT_APP_SERVER_URL}/api/admin/users`, {
      headers: { Authorization: `Bearer ${token}` },
      params
    });
  };

  return useQuery(queryKey, !payload.token ? logOutUser(navigate) : queryFn, {
    enabled: true,
    retry: 0,
    refetchOnWindowFocus: false,

    staleTime: Infinity,

    onError: (e) => {
      if (e?.response?.status === httpStatuses.unauthorized && payload.role === 'admin') {
        logOutUser(navigate);
        return;
      }
      generateToastError(toast, e);
    },
  });
};

export const useQueryGetAllUsersList = (payload, navigate, toast) => {
  const queryKey = [queryKeys.getAllUsers, payload];

  const queryFn = ({ queryKey }) =>
    axios.get(`${process.env.REACT_APP_SERVER_URL}/api/admin/users-list`, {
      headers: { Authorization: `Bearer ${queryKey[1].token}` },
    });

  return useQuery(queryKey, !payload.token ? logOutUser(navigate) : queryFn, {
    enabled: true,
    retry: 0,
    refetchOnWindowFocus: false,

    staleTime: Infinity,

    onError: (e) => {
      if (e?.response?.status === httpStatuses.unauthorized && payload.role === 'admin') {
        logOutUser(navigate);
        return;
      }
      generateToastError(toast, e);
    },
  });
};

// Manage Credentials Change Role Here

export const useQueryGetNotifications = (toast, userToken, navigate, queryClient, page = 1, limit = 50) => {
  const queryKey = [queryKeys.getAllNotifications, page, limit];
  const queryFn = ({ queryKey }) =>
    axios.get(`${process.env.REACT_APP_SERVER_URL}/api/notification/get-notifications`, {
      headers: { Authorization: `Bearer ${userToken}` },
      params: { page, limit },
    });
  return useQuery(queryKey, queryFn, {
    enabled: true,
    retry: 0,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    // refetchInterval: 10000,
    // onSuccess: (e) => {
    //   if (
    //     e?.data?.data &&
    //     JSON.stringify(e?.data?.data)?.split(false).length - 1 > 0 &&
    //     window.location.pathname.includes('meeting-invites')
    //   ) {
    //     queryClient.invalidateQueries({ queryKey: [queryKeys.getUpcomingMeetingsList] });
    //   }
    // },

    onError: (e) => {
      if (e?.response?.status === httpStatuses.unauthorized) {
        logOutUser(navigate);
        return;
      }

      generateToastError(toast, e);
    },
  });
};

export const useMutationReadAllNotifications = (
  toast,
  userToken,

  queryClient
) => {
  const queryKey = [queryKeys.readAllNotifications];
  const queryFn = () =>
    axios.patch(
      `${process.env.REACT_APP_SERVER_URL}/api/notification/update-notifications`,
      {
        isRead: true,
      },
      {
        headers: { Authorization: `Bearer ${userToken}` },
      }
    );

  return useMutation(queryKey, queryFn, {
    enabled: false,
    refetchOnWindowFocus: false,
    onSuccess: (success) => {
      // toast.success(`${success?.data?.message}`, {
      //   duration: 10000,
      // });

      queryClient.invalidateQueries({ queryKey: [queryKeys.getAllNotifications] });
    },
    onError: (error) => {
      if (error?.response?.status === httpStatuses.unauthorized && payload.role === 'admin') {
        logOutUser(navigate);
        return;
      }
      // generateToastError(toast, error);
    },
  });
};

// all timeslots flow

export const userQueryGetTimeSlots = (toast, userToken, userId) => {
  const queryKey = [queryKeys.timeSlots];

  const queryFn = ({ queryKey }) =>
    axios.get(`${process.env.REACT_APP_SERVER_URL}/api/availability/get-availability/${userId}`, {
      headers: { Authorization: `Bearer ${userToken}` },
    });

  return useQuery(queryKey, queryFn, {
    enabled: true,
    retry: 0,
    cacheTime: 0,

    refetchOnWindowFocus: false,

    staleTime: Infinity,

    onError: (e) => {
      generateToastError(toast, e);
    },
  });
};

export const useMutationModifyTimeSlots = (payload, toast, userToken, setRefactoredSlots, queryClient) => {
  const queryKey = [queryKeys.modifyTimeSlots, payload];
  const queryFn = () =>
    axios.put(`${process.env.REACT_APP_SERVER_URL}/api/availability/update-availability`, payload, {
      headers: { Authorization: `Bearer ${userToken}` },
    });
  return useMutation(queryKey, queryFn, {
    enabled: false,
    refetchOnWindowFocus: false,
    onSuccess: (success) => {
      toast.success(`${success?.data?.message}`, {
        duration: 3000,
      });

      setRefactoredSlots([]);
      
      // Invalidate time slots query to refetch updated data
      if (queryClient) {
        queryClient.invalidateQueries({ queryKey: [queryKeys.timeSlots] });
      }
    },
    onError: (error) => {
      setRefactoredSlots([]);

      if (error?.response?.status === httpStatuses.unauthorized && payload.role === 'admin') {
        logOutUser(navigate);
        return;
      }
      generateToastError(toast, error);
    },
  });
};

export const useMutationSendInviteNotifications = (payload, toast, userToken, queryClient) => {
  const queryKey = [queryKeys.inviteNotifications, payload];
  const queryFn = () =>
    axios.post(`${process.env.REACT_APP_SERVER_URL}/api/invite/send-invite`, payload, {
      headers: { Authorization: `Bearer ${userToken}` },
    });

  return useMutation(queryKey, queryFn, {
    enabled: false,
    refetchOnWindowFocus: false,
    onSuccess: (success) => {
      toast.success(`${success?.data?.message}`, {
        duration: 5000,
      });

      queryClient.invalidateQueries({ queryKey: [queryKeys.getSentInvites] });
    },
    onError: (e) => {
      if (e?.response?.status === httpStatuses.unauthorized && payload.role === 'admin') {
        logOutUser(navigate);
        return;
      }
      generateToastError(toast, e);
    },
  });
};

// all timeslots flow

//meeting Invites flow

export const useQueryGetUserInvitesList = (userToken, navigate, toast) => {
  const queryKey = [queryKeys.getUserInvites];

  const queryFn = ({ queryKey }) =>
    axios.get(`${process.env.REACT_APP_SERVER_URL}/api/invite/get-invites`, {
      headers: { Authorization: `Bearer ${userToken}` },
    });
  return useQuery(queryKey, queryFn, {
    enabled: true,
    retry: 0,
    cacheTime: 0,

    refetchOnWindowFocus: false,

    staleTime: Infinity,

    onError: (e) => {
      if (e?.response?.status === httpStatuses.unauthorized && payload.role === 'admin') {
        logOutUser(navigate);
        return;
      }
      generateToastError(toast, e);
    },
  });
};

export const useMutationModifyMeetingInvite = (
  payload,
  toast,
  userToken,
  setMeetingInvitePayload,

  navigate,
  queryClient
) => {
  const queryKey = [queryKeys.modifyMeetingInviteStatus, payload];
  const queryFn = () =>
    axios.patch(`${process.env.REACT_APP_SERVER_URL}/api/invite/update-invite`, payload, {
      headers: { Authorization: `Bearer ${userToken}` },
    });

  return useMutation(queryKey, queryFn, {
    enabled: false,
    refetchOnWindowFocus: false,
    onSuccess: (success) => {
      toast.success(`${success?.data?.message}`);
      setMeetingInvitePayload({});

      queryClient.invalidateQueries({ queryKey: [queryKeys.getUserInvites] });

      queryClient.invalidateQueries({ queryKey: [queryKeys.getUpcomingMeetingsList] });
    },
    onError: (error) => {
      if (error?.response?.status === httpStatuses.unauthorized && payload.role === 'admin') {
        logOutUser(navigate);
        return;
      }
      generateToastError(toast, error);
    },
  });
};
//meeting Invites flow

//Upcoming Meetings flow

//Upcoming Meetings flow

// sent invites api flow

export const useQueryGetSentInvites = (userToken, navigate, toast) => {
  const queryKey = [queryKeys.getSentInvites];

  const queryFn = ({ queryKey }) =>
    axios.get(`${process.env.REACT_APP_SERVER_URL}/api/invite/sent-invites`, {
      headers: { Authorization: `Bearer ${userToken}` },
    });
  return useQuery(queryKey, queryFn, {
    enabled: true,
    retry: 0,
    cacheTime: 0,

    refetchOnWindowFocus: false,

    staleTime: Infinity,

    onError: (e) => {
      if (e?.response?.status === httpStatuses.unauthorized && payload.role === 'admin') {
        logOutUser(navigate);
        return;
      }
      generateToastError(toast, e);
    },
  });
};

// sent invites api flow

// surveys api flow

export const useMutateSubmitFirstSession = (
  payload,
  navigate,
  toast,
  userToken,
  checkoutUser,
  checkoutMeetingId,
  surveyRefId,
  queryClient,
  shouldCheckoutOnSubmit = true
) => {
  const queryKey = [queryKeys.firstSessionLog, surveyRefId, payload];

  const queryFn = () =>
    axios.post(`${process.env.REACT_APP_SERVER_URL}/api/survey/save/${surveyRefId}`, payload, {
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    });

  return useMutation(queryKey, queryFn, {
    enabled: false,
    retry: 0,
    refetchOnWindowFocus: false,

    staleTime: Infinity,
    onSuccess: (success) => {
      toast.success(`Your response is recorded`, {
        duration: 3000,
      });
      
      // Hide survey modal
      const zoomModal = document.getElementById('zoom-modal');
      if (zoomModal) {
        zoomModal.style.display = 'none';
      }

      // Checkout only when requested by caller.
      if (shouldCheckoutOnSubmit && checkoutMeetingId) {
        checkoutUser(checkoutMeetingId);
      }
      
      // Invalidate survey status
      queryClient.invalidateQueries({ queryKey: [queryKeys.surveyStatus] });
      
      // Navigate to dashboard after a short delay
      setTimeout(() => {
        navigate('/dashboard/upcoming-events');
      }, 1000);
    },

    onError: (e) => {
      if (e?.response?.status === httpStatuses.unauthorized && payload.role === 'admin') {
        logOutUser(navigate);
        return;
      }
      generateToastError(toast, e);
    },
  });
};

// surveys api flow

//survey apis

export const useQueryGetSurveyStatus = (userToken, navigate, toast, surveyRefId) => {
  const queryKey = [queryKeys.surveyStatus, surveyRefId];

  const queryFn = ({ queryKey }) =>
    axios.get(`${process.env.REACT_APP_SERVER_URL}/api/survey/${surveyRefId}`, {
      headers: { Authorization: `Bearer ${userToken}` },
    });
  return useQuery(queryKey, surveyRefId ? queryFn : () => {}, {
    enabled: true,
    retry: 0,
    cacheTime: 0,

    refetchOnWindowFocus: false,

    staleTime: Infinity,

    onError: (e) => {
      if (e?.response?.status === httpStatuses.unauthorized) {
        logOutUser(navigate);
        return;
      }
      generateToastError(toast, e);
    },
  });
};

export const useQueryGetTotalUsers = (token, navigate, userRole, schoolId = '') => {
  
  const queryKey = [queryKeys.totalUsers, schoolId];

  const queryFn = ({ queryKey }) => {
    const params = {};
    if (queryKey[1]) {
      params.schoolId = queryKey[1];
    }
    return axios.get(
      `${process.env.REACT_APP_SERVER_URL}/api/${
        userRole === 'TEACHER' ? 'teacher' : userRole === 'COACH' ? 'edifier' : userRole === 'ADMIN' ? 'admin' : userRole === 'SITE-LEADER' ? 'site-leader' : userRole === 'PRINCIPAL' ? 'admin' : 'admin'
      }/analytics`,
      {
        headers: { Authorization: `Bearer ${token}` },
        params
      }
    );
  };

  return useQuery(
    queryKey,
    userRole === 'TEACHER' || userRole === 'COACH' || userRole === 'ADMIN' || userRole === 'SITE-LEADER' || userRole === 'PRINCIPAL' ? queryFn : () => {},
    {
      enabled: true,
      retry: 0,
      cacheTime: 72000,

      refetchOnWindowFocus: false,

      staleTime: Infinity,

      onError: (e) => {
        if (e?.response?.status === httpStatuses.unauthorized) {
          logOutUser(navigate);
          return;
        }
        // generateToastError(toast, e);
      },
    }
  );
};

//survey apis

// set User availability admin side

export const useMutationSetUserAvailability = (
  toast,
  userToken,
  payload,

  queryClient,
  setAvailabilityPayload
) => {
  const queryKey = [queryKeys.setUserAvailability, payload];
  const queryFn = () => {
    const userId = payload['_id'];
    delete payload['_id'];

    return axios.patch(
      `${process.env.REACT_APP_SERVER_URL}/api/admin/update-availability/${userId}`,
      {
        ...payload,
      },
      {
        headers: { Authorization: `Bearer ${userToken}` },
      }
    );
  };

  return useMutation(queryKey, queryFn, {
    enabled: false,
    refetchOnWindowFocus: false,
    onSuccess: (success) => {
      toast.success(`${success?.data?.message}`, {
        duration: 10000,
      });
      setAvailabilityPayload({});

      queryClient.invalidateQueries({ queryKey: [queryKeys.getAllUsers] });
    },
    onError: (error) => {
      if (error?.response?.status === httpStatuses.unauthorized && payload?.role === 'admin') {
        logOutUser(navigate);
        return;
      }
      // generateToastError(toast, error);
    },
  });
};

// set User availability admin side

// Batch update user availability admin side

export const useMutationBatchUpdateAvailability = (
  toast,
  userToken,
  navigate,
  queryClient,
  clearSelections
) => {
  return useMutation(
    async ({ userIds, isAvailable }) => {
      return axios.put(
        `${process.env.REACT_APP_SERVER_URL}/api/admin/batch-update-availability`,
        { userIds, isAvailable },
        {
          headers: { Authorization: `Bearer ${userToken}` },
        }
      );
    },
    {
      refetchOnWindowFocus: false,
      onSuccess: (success, variables) => {
        const action = variables.isAvailable ? 'available' : 'unavailable';
        toast.success(
          `Successfully set ${variables.userIds.length} user(s) as ${action}`,
          {
            duration: 3000,
          }
        );
        
        // Clear selections after successful update
        if (clearSelections) {
          clearSelections();
        }
        
        // Invalidate queries to refresh the list
        queryClient.invalidateQueries({ queryKey: [queryKeys.getCoachesList] });
        queryClient.invalidateQueries({ queryKey: [queryKeys.getAllUsers] });
      },
      onError: (error) => {
        if (error?.response?.status === httpStatuses.unauthorized) {
          logOutUser(navigate);
          return;
        }
        generateToastError(toast, error);
      },
    }
  );
};

// Batch update user availability admin side

//Send Bulk Messages

export const useMutationSendBulkNotifications = (
  payload,
  toast,
  userToken,
  setBulkMessagesPayload,
  setSelectedUsers,
  setValue,
  setSelectedRoles
) => {
  const queryKey = [queryKeys.sendBulkMessages, payload];
  const queryFn = () =>
    axios.post(`${process.env.REACT_APP_SERVER_URL}/api/admin/send-notifications`, payload, {
      headers: { Authorization: `Bearer ${userToken}` },
    });

  return useMutation(queryKey, queryFn, {
    enabled: false,
    retry: 0,
    refetchOnWindowFocus: false,

    staleTime: Infinity,

    onSuccess: (success) => {
      toast.success(`${success?.data?.message}`, {
        duration: 3000,
      });
      setBulkMessagesPayload({});
      setSelectedUsers([]);
      setValue('message', '');
      setSelectedRoles({ edifiers: false, teachers: false, siteLeaders: false, all: false });
    },
    onError: (error) => {
      if (error?.response?.status === httpStatuses.unauthorized) {
        logOutUser(navigate);
        return;
      }
      generateToastError(toast, error);
    },
  });
};

// Get User Profile
export const useQueryGetUserProfile = (userToken, navigate, toast) => {
  const queryKey = [queryKeys.getUserProfile];

  const queryFn = ({ queryKey }) =>
    axios.get(`${process.env.REACT_APP_SERVER_URL}/api/auth/profile`, {
      headers: { Authorization: `Bearer ${userToken}` },
    });

  return useQuery(queryKey, queryFn, {
    enabled: !!userToken,
    retry: 0,
    refetchOnWindowFocus: false,
    cacheTime: 0,
    staleTime: Infinity,
    onError: (e) => {
      if (e?.response?.status === httpStatuses.unauthorized) {
        logOutUser(navigate);
        return;
      }
      generateToastError(toast, e);
    },
  });
};

// Principal Management Hooks
export const useMutationCreatePrincipal = (payload, toast, handleClose, userToken, queryClient, navigate) => {
  const queryKey = [queryKeys.onboard, payload];
  const queryFn = () =>
    axios.post(`${process.env.REACT_APP_SERVER_URL}/api/admin/principals`, payload, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userToken}`,
      },
    });

  return useMutation(queryKey, queryFn, {
    enabled: false,
    refetchOnWindowFocus: false,
    onSuccess: (success) => {
      toast.success(
        `${success.data.message}, This account will be activated once user verify their identity using their email`,
        {
          duration: 5000,
        }
      );
      handleClose();
      queryClient.invalidateQueries({ queryKey: [queryKeys.getPrincipalsList] });
    },
    onError: (error) => {
      if (error?.response?.status === httpStatuses.unauthorized) {
        logOutUser(navigate);
        return;
      }
      generateToastError(toast, error);
    },
  });
};

export const useQueryGetPrincipalsList = (payload, navigate, toast) => {
  const queryKey = [queryKeys.getPrincipalsList, payload];

  const queryFn = ({ queryKey }) => {
    const { token, page = 1, limit = 10, ...otherParams } = queryKey[1];
    
    const params = {
      page,
      limit,
      ...otherParams
    };

    return axios.get(`${process.env.REACT_APP_SERVER_URL}/api/admin/principals`, {
      headers: { Authorization: `Bearer ${token}` },
      params
    });
  };

  return useQuery(queryKey, !payload.token ? logOutUser(navigate) : queryFn, {
    enabled: true,
    retry: 0,
    cacheTime: 0,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    onError: (e) => {
      if (e?.response?.status === httpStatuses.unauthorized) {
        logOutUser(navigate);
        return;
      }
      generateToastError(toast, e);
    },
  });
};

export const useMutationUpdatePrincipal = (payload, toast, handleClose, userToken, setPrincipalToEdit, navigate, queryClient) => {
  const queryKey = [queryKeys.updatePrincipal, payload];
  const queryFn = () =>
    axios.put(
      `${process.env.REACT_APP_SERVER_URL}/api/admin/principals/${payload.principalId}`, 
      payload.data,
      {
        headers: { Authorization: `Bearer ${userToken}` },
      }
    );

  return useMutation(queryKey, queryFn, {
    enabled: false,
    refetchOnWindowFocus: false,
    onSuccess: (success) => {
      toast.success(`${success.data.message}`, {
        duration: 5000,
      });
      setPrincipalToEdit({});
      queryClient.invalidateQueries({ queryKey: [queryKeys.getPrincipalsList] });
      handleClose();
    },
    onError: (error) => {
      if (error?.response?.status === httpStatuses.unauthorized) {
        logOutUser(navigate);
        return;
      }
      generateToastError(toast, error);
    },
  });
};

export const useQueryDeletePrincipal = (payload, navigate, setDeleteOperationOpen, toast, queryClient) => {
  const queryKey = [queryKeys.deletePrincipal, payload];

  const queryFn = ({ queryKey }) =>
    axios.delete(`${process.env.REACT_APP_SERVER_URL}/api/admin/principals/${queryKey[1]._id}`, {
      headers: { Authorization: `Bearer ${queryKey[1].token}` },
    });

  return useQuery(queryKey, !payload.token ? logOutUser(navigate) : queryFn, {
    enabled: false,
    retry: 0,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    onSuccess: () => {
      setDeleteOperationOpen(false);
      toast.success('Principal deleted successfully');
      queryClient.invalidateQueries({ queryKey: [queryKeys.getPrincipalsList] });
    },
    onError: (e) => {
      setDeleteOperationOpen(false);
      if (e?.message?.includes('Network Error')) {
        toast.error('Backend Service Not Found');
        return;
      }
      if (e?.response?.status === httpStatuses.unauthorized) {
        logOutUser(navigate);
        return;
      }
      if (e?.response?.status === httpStatuses.error) {
        toast.error('Either Server is Down or passed Item is already deleted');
        return;
      }
      generateToastError(toast, e);
    },
  });
};

//Send Bulk Messages
